import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../api';

// ============================================================================
// LOADING COMPONENT
// ============================================================================

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center py-12 gap-3">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
    <p className="text-sm font-medium text-slate-500 animate-pulse">Loading information...</p>
  </div>
);

// ============================================================================
// ACADEMIC CONTEXT SELECTOR
// ============================================================================

const AcademicContextSelector = ({
  tenantId,
  label,
  onContextChange,
  initialContext,
  hideLevel = false,
  required = true,
  compact = false,
}) => {
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [terms, setTerms] = useState([]);

  const [programTypeId, setProgramTypeId] = useState(initialContext?.programTypeId || '');
  const [programId, setProgramId] = useState(initialContext?.programId || '');
  const [academicYearId, setAcademicYearId] = useState(initialContext?.academicYearId || '');
  const [semesterId, setSemesterId] = useState(initialContext?.semesterId || '');
  const [termId, setTermId] = useState(initialContext?.termId || '');
  const [isSemesterBased, setIsSemesterBased] = useState(initialContext?.isSemesterBased ?? null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const prevContextRef = useRef(null);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => setProgramTypes(res.data.programTypes || []))
      .catch(() => setError('Failed to load program types'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !programTypeId) {
      setPrograms([]);
      return;
    }
    api.get('/api/programs', { params: { tenantId, programTypeId, status: 'active' } })
      .then(res => setPrograms(res.data.programs || []))
      .catch(() => setError('Failed to load programs'));
  }, [tenantId, programTypeId]);

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      api.get('/api/academic-year/all-active', { params: { tenantId } }),
      api.get('/api/academic-year/all-completed', { params: { tenantId } })
    ])
      .then(([activeRes, completedRes]) => {
        const activeYears = activeRes.data.academicYears || activeRes.data.activeAcademicYears || [];
        const completedYears = completedRes.data.academicYears || [];
        setAcademicYears([...activeYears, ...completedYears]);
      })
      .catch(() => setError('Failed to load academic years'));
  }, [tenantId]);

  useEffect(() => {
    if (!academicYearId) {
      setSemesters([]);
      setTerms([]);
      setIsSemesterBased(null);
      return;
    }

    setLoading(true);
    setSemesters([]);
    setTerms([]);
    setIsSemesterBased(null);

    api.get(`/api/classManager/semesters/${academicYearId}`)
      .then(res => {
        const sems = res.data.semesters || [];
        if (sems.length > 0) {
          setSemesters(sems);
          setIsSemesterBased(true);
        } else {
          api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
            .then(res2 => {
              const trms = res2.data.terms || [];
              setTerms(trms);
              setIsSemesterBased(false);
            })
            .catch(() => setError('Failed to load terms'));
        }
      })
      .catch(() => {
        api.get(`/api/academic-year/${academicYearId}/terms`, { params: { tenantId } })
          .then(res2 => {
            const trms = res2.data.terms || [];
            setTerms(trms);
            setIsSemesterBased(false);
          })
          .catch(() => setError('Failed to load semesters/terms'));
      })
      .finally(() => setLoading(false));
  }, [academicYearId, tenantId]);

  const contextValue = useMemo(() => {
    if (!programTypeId || !programId || !academicYearId) return null;
    if (!hideLevel) {
      if ((isSemesterBased === true && !semesterId) || (isSemesterBased === false && !termId)) return null;
    }

    const selectedProgramType = programTypes.find(pt => pt._id === programTypeId);
    const selectedProgram = programs.find(p => p._id === programId);
    const selectedAcademicYear = academicYears.find(y => y._id === academicYearId);
    const selectedSemester = semesters.find(s => s._id === semesterId);
    const selectedTerm = terms.find(t => t._id === termId);

    return {
      programTypeId,
      programTypeName: selectedProgramType?.name || '',
      programId,
      programName: selectedProgram?.name || '',
      academicYearId,
      academicYearName: selectedAcademicYear?.name || selectedAcademicYear?.academicYear || '',
      semesterId: isSemesterBased === true ? semesterId : null,
      semesterName: isSemesterBased === true ? (selectedSemester?.name || selectedSemester?.semesterLabel || `Semester ${selectedSemester?.semesterNumber}` || '') : '',
      termId: isSemesterBased === false ? termId : null,
      termName: isSemesterBased === false ? (selectedTerm?.name || selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}` || '') : '',
      isSemesterBased,
      levelId: isSemesterBased === true ? semesterId : termId,
      levelName: isSemesterBased === true ? (selectedSemester?.name || selectedSemester?.semesterLabel || `Semester ${selectedSemester?.semesterNumber}` || '') : (selectedTerm?.name || selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}` || ''),
    };
  }, [programTypeId, programId, academicYearId, semesterId, termId, isSemesterBased, programTypes, programs, academicYears, semesters, terms, hideLevel]);

  useEffect(() => {
    if (contextValue && onContextChange) {
      const contextStr = JSON.stringify(contextValue);
      if (contextStr !== prevContextRef.current) {
        prevContextRef.current = contextStr;
        onContextChange(contextValue);
      }
    }
  }, [contextValue, onContextChange]);

  if (loading && !programTypeId) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          <p className="text-xs font-medium text-slate-500">Loading structure options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-slate-200 bg-white/80 backdrop-blur-sm ${compact ? 'p-3' : 'p-5'} shadow-sm space-y-4`}>
      {label && <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">{label}</h4>}
      {error && (
        <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 border border-rose-100 font-medium">
          ⚠️ {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            Course Level Type {required && <span className="text-rose-500">*</span>}
          </label>
          <select
            value={programTypeId}
            onChange={(e) => {
              setProgramTypeId(e.target.value);
              setProgramId('');
              setAcademicYearId('');
              setSemesterId('');
              setTermId('');
              setIsSemesterBased(null);
            }}
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800 transition focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
          >
            <option value="">Select Level</option>
            {programTypes.map(pt => (
              <option key={pt._id} value={pt._id}>{pt.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            Program / Degree {required && <span className="text-rose-500">*</span>}
          </label>
          <select
            value={programId}
            onChange={(e) => {
              setProgramId(e.target.value);
              setAcademicYearId('');
              setSemesterId('');
              setTermId('');
              setIsSemesterBased(null);
            }}
            disabled={!programTypeId}
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800 transition focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">Select Program</option>
            {programs.map(p => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">
            Current Academic Year {required && <span className="text-rose-500">*</span>}
          </label>
          <select
            value={academicYearId}
            onChange={(e) => {
              setAcademicYearId(e.target.value);
              setSemesterId('');
              setTermId('');
              setIsSemesterBased(null);
            }}
            disabled={!programId}
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800 transition focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">Select Year</option>
            {academicYears.map(ay => (
              <option key={ay._id} value={ay._id}>
                {ay.name || ay.academicYear}
                {ay.status === 'completed' && ' (Finished ✓)'}
              </option>
            ))}
          </select>
        </div>

        {!hideLevel && (
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">
              {isSemesterBased === true ? 'Semester' : isSemesterBased === false ? 'Term Part' : 'Term / Term Part'}
              {required && <span className="text-rose-500">*</span>}
            </label>
            {isSemesterBased === true && (
              <select
                value={semesterId}
                onChange={(e) => setSemesterId(e.target.value)}
                disabled={!academicYearId || semesters.length === 0}
                className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800 transition focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Select Semester</option>
                {semesters.map(sem => (
                  <option key={sem._id} value={sem._id}>
                    {sem.name || sem.semesterLabel || `Semester ${sem.semesterNumber}`}
                    {sem.status === 'completed' && ' (Finished ✓)'}
                  </option>
                ))}
              </select>
            )}
            {isSemesterBased === false && (
              <select
                value={termId}
                onChange={(e) => setTermId(e.target.value)}
                disabled={!academicYearId || terms.length === 0}
                className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800 transition focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Select Term</option>
                {terms.map(term => (
                  <option key={term._id} value={term._id}>
                    {term.name || term.termLabel || `Term ${term.termNumber}`}
                    {term.status === 'completed' && ' (Finished ✓)'}
                  </option>
                ))}
              </select>
            )}
            {isSemesterBased === null && academicYearId && (
              <div className="text-xs font-medium text-amber-600 py-2 flex items-center gap-1.5 animate-pulse">
                <div className="h-2 w-2 rounded-full bg-amber-500" /> Finding Terms...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STUDENT DETAILS EXPANDABLE ROW - USING WEIGHTED MARKS
// ============================================================================

const StudentDetailsRow = ({ student }) => {
  const [expanded, setExpanded] = useState(false);
  const subjects = student.subjects || [];

  // Helper to get CIE display (weighted)
  const getCieDisplay = (sub) => {
    const cie = sub.cie || {};
    
    const marks = cie.weightedMarks ?? cie.marks ?? 0;
    const maxMarks = cie.weightedMax ?? cie.maxMarks ?? 0;
    const percentage = cie.weightedPercentage ?? cie.percentage ?? 0;
    const entered = cie.entered || false;
    
    if (!entered || marks === 0) {
      return { display: 'N/A', marks: 0, maxMarks: 0, percentage: 0, entered: false };
    }
    
    return {
      display: `${marks}/${maxMarks} (${percentage}%)`,
      marks,
      maxMarks,
      percentage,
      entered: true
    };
  };

  // ✅ FIXED: Use WEIGHTED marks for display
  const getEndExamDisplay = (sub) => {
    const endExam = sub.endExam || {};
    const assessmentType = sub.assessmentType || 'both';
    
    // ✅ Use weightedMarks to check if marks exist
    const hasMarks = endExam.entered === true && endExam.weightedMarks > 0;
    
    if (!hasMarks) {
      const isApplicable = assessmentType === 'both' || 
                          assessmentType === 'end_exam_only' || 
                          assessmentType === 'external_only';
      
      if (!isApplicable) {
        return { display: 'Not Applicable', marks: null, maxMarks: null, percentage: null, entered: false };
      }
      return { display: 'N/A', marks: 0, maxMarks: 0, percentage: 0, entered: false };
    }
    
    // ✅ Use WEIGHTED values for display
    const marks = endExam.weightedMarks ?? 0;
    const maxMarks = endExam.weightedMax ?? 0;
    const percentage = endExam.weightedPercentage ?? 0;
    
    return {
      display: `${marks}/${maxMarks} (${percentage}%)`,
      marks,
      maxMarks,
      percentage,
      entered: true
    };
  };

  // Helper to get overall (weighted)
  const getOverallDisplay = (sub) => {
    const overall = sub.overall || {};
    
    const marks = overall.weightedMarks ?? overall.marks ?? 0;
    const maxMarks = overall.weightedMax ?? overall.maxMarks ?? 0;
    const percentage = overall.weightedPercentage ?? overall.percentage ?? 0;
    const isPassed = overall.isPassed ?? sub.isPassed ?? false;
    
    const cieEntered = sub.cie?.entered || false;
    const endExamEntered = sub.endExam?.entered || false;
    const hasMarks = cieEntered || endExamEntered;
    
    if (!hasMarks) {
      return { display: 'N/A', marks: 0, maxMarks: 0, percentage: 0, isPassed: false, hasMarks: false };
    }
    
    return {
      display: `${marks}/${maxMarks} (${percentage}%)`,
      marks,
      maxMarks,
      percentage,
      isPassed,
      hasMarks: true
    };
  };

  return (
    <React.Fragment>
      {/* Desktop Layout Row */}
      <tr 
        className="hidden md:table-row hover:bg-slate-50 border-b border-slate-100 transition-colors cursor-pointer" 
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-3.5 text-sm font-mono font-semibold text-slate-900">{student.USN}</td>
        <td className="px-4 py-3.5 text-sm font-medium text-slate-700">{student.name}</td>
        <td className="px-4 py-3.5 text-sm text-slate-500">{student.className}</td>
        <td className="px-4 py-3.5 text-center text-sm font-medium text-slate-600">{student.metrics?.ciePercentage || 0}%</td>
        <td className="px-4 py-3.5 text-center text-sm font-medium text-slate-600">{student.metrics?.endExamPercentage || 0}%</td>
        <td className="px-4 py-3.5 text-center text-sm font-bold text-slate-800">{student.metrics?.overallPercentage || 0}%</td>
        <td className="px-4 py-3.5 text-center">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            student.metrics?.isPassed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
          }`}>
            {student.metrics?.isPassed ? 'Passed' : 'Failed'}
          </span>
        </td>
        <td className="px-4 py-3.5 text-center">
          <span className="text-xs font-medium text-indigo-600 hover:text-indigo-900">
            {expanded ? 'Hide ▲' : 'Details ▼'}
          </span>
        </td>
      </tr>

      {/* Mobile Card */}
      <tr className="md:hidden block w-full border-b border-slate-100">
        <td colSpan="8" className="block p-2">
          <div 
            className="p-4 bg-white rounded-xl border border-slate-100 shadow-sm space-y-3 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="flex justify-between items-start">
              <div>
                <span className="text-xs font-mono font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                  {student.USN}
                </span>
                <h4 className="text-sm font-bold text-slate-800 mt-1">{student.name}</h4>
                <p className="text-xs text-slate-400">{student.className}</p>
              </div>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                student.metrics?.isPassed ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
              }`}>
                {student.metrics?.isPassed ? 'Passed' : 'Failed'}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-lg p-2 text-center text-xs">
              <div>
                <p className="text-slate-400 font-medium">CIE</p>
                <p className="font-bold text-slate-700">{student.metrics?.ciePercentage || 0}%</p>
              </div>
              <div>
                <p className="text-slate-400 font-medium">End Exam</p>
                <p className="font-bold text-slate-700">{student.metrics?.endExamPercentage || 0}%</p>
              </div>
              <div>
                <p className="text-slate-400 font-medium">Total</p>
                <p className="font-bold text-indigo-600">{student.metrics?.overallPercentage || 0}%</p>
              </div>
            </div>
            <div className="text-center pt-1 border-t border-slate-100 text-xs font-semibold text-indigo-600">
              {expanded ? 'Hide Details' : 'View Subject Breakdowns'}
            </div>
          </div>
        </td>
      </tr>

      {/* Expanded Block - Using Weighted Values */}
      {expanded && (
        <tr className="bg-slate-50/70 md:table-row block">
          <td colSpan="8" className="px-3 py-3 md:px-6 block md:table-cell">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-inner space-y-3">
              <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                <span>📚</span> Subject Performance List
              </h5>
              {subjects.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No subject courses registered for this student.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-100">
                  <table className="min-w-full divide-y divide-slate-100 text-xs">
                    <thead className="bg-slate-50 text-slate-500 font-semibold">
                      <tr>
                        <th className="px-3 py-2 text-left">Subject</th>
                        <th className="px-3 py-2 text-left">Code</th>
                        <th className="px-3 py-2 text-center">Type</th>
                        <th className="px-3 py-2 text-center">CIE Marks</th>
                        <th className="px-3 py-2 text-center">End Exam Marks</th>
                        <th className="px-3 py-2 text-center">Overall</th>
                        <th className="px-3 py-2 text-center">Result</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                      {subjects.map((sub, idx) => {
                        const assessmentType = sub.assessmentType || 'both';
                        
                        const cieData = getCieDisplay(sub);
                        const endExamData = getEndExamDisplay(sub);
                        const overallData = getOverallDisplay(sub);
                        const hasMarks = cieData.entered || endExamData.entered;

                        let typeLabel = assessmentType;
                        if (assessmentType === 'both') typeLabel = 'Both';
                        else if (assessmentType === 'cie_only' || assessmentType === 'internal_only') typeLabel = 'CIE Only';
                        else if (assessmentType === 'end_exam_only' || assessmentType === 'external_only') typeLabel = 'End Exam Only';
                        
                        let typeColor = 'bg-indigo-50 text-indigo-700';
                        if (assessmentType === 'cie_only' || assessmentType === 'internal_only') typeColor = 'bg-purple-50 text-purple-700';
                        else if (assessmentType === 'end_exam_only' || assessmentType === 'external_only') typeColor = 'bg-amber-50 text-amber-700';

                        return (
                          <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-3 py-2 font-medium text-slate-800">{sub.subjectName}</td>
                            <td className="px-3 py-2 font-mono text-slate-400">{sub.subjectCode}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${typeColor}`}>
                                {typeLabel}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {cieData.entered ? (
                                <span className="font-medium text-slate-700">{cieData.display}</span>
                              ) : (
                                <span className="text-slate-300">N/A</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {endExamData.entered ? (
                                <span className="font-medium text-slate-700">{endExamData.display}</span>
                              ) : endExamData.display === 'Not Applicable' ? (
                                <span className="text-slate-300">Not Applicable</span>
                              ) : (
                                <span className="text-slate-300">N/A</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center font-bold text-slate-800">
                              {hasMarks ? overallData.display : 'N/A'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {hasMarks ? (
                                <span className={`font-bold ${overallData.isPassed ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {overallData.isPassed ? '✓ Pass' : '✕ Fail'}
                                </span>
                              ) : (
                                <span className="text-slate-300">N/A</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// ============================================================================
// BATCH LIST COMPONENT
// ============================================================================

const BatchList = ({ tenantId, onSelectBatch }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBatches = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/coe/student-promotion/batches', { params: { tenantId } });
      setBatches(res.data.data || []);
    } catch (err) {
      setError('Failed to load batches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-rose-600 text-sm font-medium p-4 bg-rose-50 rounded-xl">{error}</div>;
  if (batches.length === 0) return <div className="text-slate-400 text-sm text-center py-8">No saved promotion groups found.</div>;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3 text-left">Group Code</th>
            <th className="px-4 py-3 text-left">Program</th>
            <th className="px-4 py-3 text-left">Academic Year</th>
            <th className="px-4 py-3 text-center">Total Students</th>
            <th className="px-4 py-3 text-center">Process Status</th>
            <th className="px-4 py-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white text-slate-700">
          {batches.map((batch) => (
            <tr key={batch.batchId} className="hover:bg-slate-50/80 transition-colors">
              <td className="px-4 py-3.5 font-mono text-xs font-semibold text-slate-500">{batch.batchId}</td>
              <td className="px-4 py-3.5 font-medium text-slate-800">{batch.programName}</td>
              <td className="px-4 py-3.5 text-slate-500">{batch.academicYearName}</td>
              <td className="px-4 py-3.5 text-center font-semibold text-slate-800">{batch.totalStudents}</td>
              <td className="px-4 py-3.5 text-center">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  batch.isPromoted ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  batch.isPublished ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}>
                  {batch.isPromoted ? 'Finished ✓' : batch.isPublished ? 'Published Result' : 'Draft'}
                </span>
              </td>
              <td className="px-4 py-3.5 text-center">
                <div className="inline-flex gap-2">
                  <button
                    onClick={() => onSelectBatch(batch.batchId)}
                    className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-200"
                  >
                    View
                  </button>
                  {batch.isPublished && !batch.isPromoted && (
                    <button
                      onClick={() => onSelectBatch(batch.batchId)}
                      className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                    >
                      Promote Group
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// MAIN PROMOTE STUDENTS COMPONENT
// ============================================================================

const RegularPromotion = () => {
  console.log('[RegularPromotion] Component rendering');

  // ===== USER STATE =====
  const [tenantId, setTenantId] = useState('');
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');

  // ===== ACADEMIC CONTEXT =====
  const [sourceContext, setSourceContext] = useState(null);
  const [targetContext, setTargetContext] = useState(null);
  const [yearBackTargetContext, setYearBackTargetContext] = useState(null);

  // ===== PROMOTION WORKFLOW =====
  const [students, setStudents] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [publishedBatchId, setPublishedBatchId] = useState(null);
  const [promotionResults, setPromotionResults] = useState(null);
  const [backlogs, setBacklogs] = useState([]);

  // ===== BATCH MANAGEMENT =====
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [batchResults, setBatchResults] = useState(null);
  const [showBatchView, setShowBatchView] = useState(false);
  const [showBatchList, setShowBatchList] = useState(false);

  // ===== UI STATES =====
  const [loading, setLoading] = useState(false);
  const [contextCheckLoading, setContextCheckLoading] = useState(false); 
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeStep, setActiveStep] = useState(1);
  const [showResults, setShowResults] = useState(false);

  // ===== MANUAL OVERRIDE =====
  const [manualOverride, setManualOverride] = useState(false);

  // ===== CONFIRMATION MODAL =====
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  // ===== TABS =====
  const [activeTab, setActiveTab] = useState('promotion');

  // ===== TRACK RESUMED STATUS =====
  const [isResumedSession, setIsResumedSession] = useState(false);

  const STEPS = [
    { id: 1, label: 'Choose Group', icon: '📋' },
    { id: 2, label: 'Verify Marks', icon: '👨‍🎓' },
    { id: 3, label: 'Preview Changes', icon: '👀' },
    { id: 4, label: 'Publish List', icon: '📤' },
    { id: 5, label: 'Complete Move', icon: '🚀' },
  ];

  // ===== GET USER FROM STORAGE =====
  const getUserFromStorage = useCallback(() => {
    try {
      const userStr = localStorage.getItem('user') ||
                      localStorage.getItem('userData') ||
                      localStorage.getItem('currentUser') ||
                      sessionStorage.getItem('user');

      if (userStr) {
        const user = JSON.parse(userStr);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.firstName || user.fullName || 'User'
        };
      }

      const userIdFromStorage = localStorage.getItem('userId') ||
                                localStorage.getItem('user_id') ||
                                sessionStorage.getItem('userId');

      if (userIdFromStorage) {
        return { id: userIdFromStorage, name: 'User' };
      }

      return { id: null, name: 'Unknown' };
    } catch (e) {
      console.error('[PromoteStudents] Error reading user from storage:', e);
      return { id: null, name: 'Unknown' };
    }
  }, []);

  // ===== API HELPERS =====
  const handleApiError = (err, defaultMessage) => {
    console.error('[PromoteStudents] API Error:', err);
    const msg = err.response?.data?.message || defaultMessage;
    setError(msg);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setError(''), 6000);
  };

  const showSuccess = (message) => {
    setSuccess(message);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setSuccess(''), 6000);
  };

  // ===== FETCH STUDENTS =====
  const fetchStudents = async () => {
    if (!tenantId || !sourceContext) {
      setError('Please choose your class information first.');
      return;
    }

    setLoading(true);
    setError('');

    const params = {
      tenantId,
      programTypeId: sourceContext.programTypeId,
      programId: sourceContext.programId,
      academicYearId: sourceContext.academicYearId,
    };

    if (sourceContext.isSemesterBased) {
      params.semesterId = sourceContext.semesterId;
    } else {
      params.termId = sourceContext.termId;
    }

    try {
      const res = await api.get('/api/coe/student-promotion/students-with-marks', { params });
      const studentData = res.data.data?.students || [];
      setStudents(studentData);
      setActiveStep(2);
      showSuccess(`Loaded ${studentData.length} students successfully.`);
    } catch (err) {
      handleApiError(err, 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  // ===== FETCH POLICIES =====
  const fetchPolicies = useCallback(async () => {
    if (!tenantId || !sourceContext) return;

    try {
      const res = await api.get('/api/coe/student-promotion/available-policies', {
        params: {
          tenantId,
          programId: sourceContext.programId,
        },
      });
      const policyData = res.data.data || [];
      setPolicies(policyData);
      if (policyData.length > 0) {
        setSelectedPolicyId(policyData[0]._id);
      }
    } catch (err) {
      handleApiError(err, 'Failed to load rule maps');
    }
  }, [tenantId, sourceContext]);

  // ===== FETCH BACKLOGS =====
  const fetchBacklogs = useCallback(async (batchId) => {
    try {
      const res = await api.get(`/api/coe/student-promotion/backlogs/${batchId}`, {
        params: { tenantId },
      });
      setBacklogs(res.data.data?.backlogs || []);
    } catch (err) {
      console.error('[PromoteStudents] Failed to fetch backlogs:', err);
    }
  }, [tenantId]);

  // ===== LOOKUP PENDING ACTIONS =====
  const checkExistingPublishedResults = useCallback(async () => {
    if (!tenantId || !sourceContext) return false;

    setContextCheckLoading(true);

    try {
      const res = await api.get('/api/coe/student-promotion/batches', {
        params: { tenantId },
      });

      const allBatches = res.data.data || [];

      const matchingBatch = allBatches.find(batch => {
        const programMatch = batch.programId?.toString() === sourceContext.programId?.toString();
        const ayMatch = batch.academicYearId?.toString() === sourceContext.academicYearId?.toString();

        let levelMatch = false;
        if (sourceContext.isSemesterBased && sourceContext.semesterId) {
          levelMatch = batch.semesterId?.toString() === sourceContext.semesterId?.toString();
        } else if (!sourceContext.isSemesterBased && sourceContext.termId) {
          levelMatch = batch.termId?.toString() === sourceContext.termId?.toString();
        }

        return programMatch && ayMatch && levelMatch && batch.isPublished && !batch.isPromoted;
      });

      if (matchingBatch) {
        const batchRes = await api.get(
          `/api/coe/student-promotion/batch/${matchingBatch.batchId}`,
          { params: { tenantId } }
        );
        const batchData = batchRes.data.data;

        setPublishedBatchId(matchingBatch.batchId);
        setBatchResults(batchData);
        setIsResumedSession(true); 

        if (batchData.isPromoted) {
          setActiveStep(5);
          setShowResults(true);
          setPromotionResults(batchData);
          showSuccess('This promotion group is completely finished!');
        } else {
          setActiveStep(5);
          showSuccess(`Found a published list waiting to be completed (Batch Code: ${matchingBatch.batchId}). Please select destination details below.`);
        }

        await fetchBacklogs(matchingBatch.batchId);
        return true;
      }

      setIsResumedSession(false);
      return false;
    } catch (err) {
      console.error('[PromoteStudents] Failed to check published results:', err);
      setIsResumedSession(false);
      return false;
    } finally {
      setContextCheckLoading(false);
    }
  }, [tenantId, sourceContext, fetchBacklogs]);

  // ===== FETCH BATCH RESULTS =====
  const fetchBatchResults = useCallback(async (batchId) => {
    if (!tenantId || !batchId) return;

    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/coe/student-promotion/batch/${batchId}`, {
        params: { tenantId },
      });
      const data = res.data.data;
      setBatchResults(data);
      setSelectedBatchId(batchId);
      setShowBatchView(true);
      setActiveTab('batches');
    } catch (err) {
      handleApiError(err, 'Failed to load system group data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // ===== PREVIEW PROMOTION =====
  const handlePreview = async () => {
    if (!tenantId || !sourceContext || !selectedPolicyId) {
      setError('Please select both the class group and the promotion rule.');
      return;
    }

    setLoading(true);
    setError('');

    const body = {
      tenantId,
      programTypeId: sourceContext.programTypeId,
      programId: sourceContext.programId,
      academicYearId: sourceContext.academicYearId,
      policyId: selectedPolicyId,
    };

    if (sourceContext.isSemesterBased) {
      body.semesterId = sourceContext.semesterId;
    } else {
      body.termId = sourceContext.termId;
    }

    try {
      const res = await api.post('/api/coe/student-promotion/promotion-preview', body);
      setPreviewData(res.data.data);
      setIsResumedSession(false);
      setActiveStep(3);
      showSuccess('Preview generated below. Review decisions before committing.');
    } catch (err) {
      handleApiError(err, 'Failed to create calculation preview');
    } finally {
      setLoading(false);
    }
  };

  // ===== PUBLISH PROMOTION =====
  const handlePublish = async () => {
    if (!tenantId || !sourceContext || !selectedPolicyId) {
      setError('Missing selections.');
      return;
    }

    if (!userId) {
      setError('Your system login session has expired. Re-login.');
      return;
    }

    setLoading(true);
    setError('');

    const body = {
      tenantId,
      programTypeId: sourceContext.programTypeId,
      programId: sourceContext.programId,
      academicYearId: sourceContext.academicYearId,
      policyId: selectedPolicyId,
      confirmedBy: userId,
      remarks: `Published by ${userName} on ${new Date().toLocaleDateString()}`,
    };

    if (sourceContext.isSemesterBased) {
      body.semesterId = sourceContext.semesterId;
    } else {
      body.termId = sourceContext.termId;
    }

    try {
      const res = await api.post('/api/coe/student-promotion/promotion-publish', body);
      const data = res.data.data;
      setPublishedBatchId(data.batchId);
      setIsResumedSession(false);
      setActiveStep(5);
      showSuccess(`Calculated results saved and published. Batch Identifier: ${data.batchId}`);

      if (data.batchId) {
        await fetchBacklogs(data.batchId);
        const batchRes = await api.get(
          `/api/coe/student-promotion/batch/${data.batchId}`,
          { params: { tenantId } }
        );
        setBatchResults(batchRes.data.data);
      }
    } catch (err) {
      handleApiError(err, 'Failed to save/publish list updates');
    } finally {
      setLoading(false);
    }
  };

  // ===== PROMOTE STUDENTS =====
  const handlePromote = async () => {
    if (!tenantId || !publishedBatchId) {
      setError('No active student group ready to move.');
      return;
    }

    if (!userId) {
      setError('Session missing identifier logs.');
      return;
    }

    if (!sourceContext) {
      setError('Starting details required.');
      return;
    }

    if (!targetContext) {
      setError('Please choose the destination class target context.');
      return;
    }

    setLoading(true);
    setError('');

    const body = {
      tenantId,
      batchId: publishedBatchId,
      sourceProgramId: sourceContext.programId,
      sourceProgramTypeId: sourceContext.programTypeId,
      sourceAcademicYearId: sourceContext.academicYearId,
      sourceIsSemesterBased: sourceContext.isSemesterBased || false,
    };

    if (sourceContext.isSemesterBased) {
      body.sourceSemesterId = sourceContext.semesterId;
    } else {
      body.sourceTermId = sourceContext.termId;
    }

    body.targetProgramId = targetContext.programId;
    body.targetProgramTypeId = targetContext.programTypeId;
    body.targetAcademicYearId = targetContext.academicYearId;
    body.targetIsSemesterBased = targetContext.isSemesterBased || false;

    if (targetContext.isSemesterBased) {
      body.targetSemesterId = targetContext.semesterId;
    } else {
      body.targetTermId = targetContext.termId;
    }

    if (manualOverride && yearBackTargetContext) {
      body.manualOverride = true;
      body.yearBackTargetIsSemesterBased = yearBackTargetContext.isSemesterBased || false;

      if (yearBackTargetContext.isSemesterBased) {
        body.yearBackTargetSemesterId = yearBackTargetContext.semesterId;
      } else {
        body.yearBackTargetTermId = yearBackTargetContext.termId;
      }
    }

    body.promotedBy = userId;
    body.onlyPromotedStudents = false;
    body.isProvisional = false;
    body.skipEvaluation = false;
    body.remarks = `Promoted by ${userName} on ${new Date().toLocaleDateString()}`;

    try {
      const res = await api.post('/api/coe/student-promotion/promote-students', body);
      setPromotionResults(res.data.data);
      setActiveStep(5);
      setShowResults(true);
      showSuccess(`Success! Moved ${res.data.data?.promotedCount || 0} eligible students to their new class slots.`);

      const batchRes = await api.get(
        `/api/coe/student-promotion/batch/${publishedBatchId}`,
        { params: { tenantId } }
      );
      setBatchResults(batchRes.data.data);
    } catch (err) {
      handleApiError(err, 'Failed to execute structural promotion move');
    } finally {
      setLoading(false);
    }
  };

  // ===== ROLLBACK BATCH =====
  const handleRollbackBatch = async (batchId) => {
    if (!tenantId || !batchId) {
      setError('Missing rollback configuration.');
      return;
    }

    setModalData({
      action: 'rollbackBatch',
      title: 'Undo Entire Group Process?',
      message: `Warning: This action completely cancels all temporary allocations or rules saved under "${batchId}". Data resets permanently.`,
      confirmText: 'Yes, Reset Completely',
      cancelText: 'Go Back',
      data: { batchId }
    });
    setShowConfirmModal(true);
  };

  // ===== ROLLBACK INDIVIDUAL STUDENT =====
  const handleRollbackIndividual = async (studentId, batchId) => {
    if (!tenantId || !studentId) {
      setError('Student identification missing.');
      return;
    }

    setModalData({
      action: 'rollbackIndividual',
      title: 'Undo This Single Student Move?',
      message: `This will separate this student from this computed group roster list. This cannot be undone.`,
      confirmText: 'Yes, Reset Student',
      cancelText: 'Cancel',
      data: { studentId, batchId }
    });
    setShowConfirmModal(true);
  };

  const confirmRollback = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setError('');

    try {
      if (modalData.action === 'rollbackBatch') {
        await api.post('/api/coe/student-promotion/rollback', {
          tenantId,
          batchId: modalData.data.batchId,
          rolledBackBy: userId,
          reason: `Rolled back by ${userName}`,
        });
        setPublishedBatchId(null);
        setPromotionResults(null);
        setBatchResults(null);
        setShowResults(false);
        setIsResumedSession(false);
        setActiveStep(1);
        setStudents([]);
        setPreviewData(null);
        showSuccess('Group logs reverted successfully.');
      } else if (modalData.action === 'rollbackIndividual') {
        await api.post('/api/coe/student-promotion/rollback-individual', {
          tenantId,
          studentId: modalData.data.studentId,
          rolledBackBy: userId,
          reason: `Rolled back by ${userName}`,
          batchId: modalData.data.batchId,
        });
        showSuccess('Single student adjustments reverted.');
        if (selectedBatchId) {
          await fetchBatchResults(selectedBatchId);
        } else if (publishedBatchId) {
          const batchRes = await api.get(
            `/api/coe/student-promotion/batch/${publishedBatchId}`,
            { params: { tenantId } }
          );
          setBatchResults(batchRes.data.data);
        }
      }
    } catch (err) {
      handleApiError(err, 'Failed to reverse records status');
    } finally {
      setLoading(false);
      setModalData(null);
    }
  };

  // ===== START NEW PROMOTION =====
  const handleStartNew = () => {
    if (window.confirm('Reset current layout selections and start a clean operation?')) {
      setPublishedBatchId(null);
      setBatchResults(null);
      setPromotionResults(null);
      setShowResults(false);
      setActiveStep(1);
      setStudents([]);
      setPreviewData(null);
      setBacklogs([]);
      setTargetContext(null);
      setYearBackTargetContext(null);
      setManualOverride(false);
      setShowBatchView(false);
      setActiveTab('promotion');
      setIsResumedSession(false);
      showSuccess('Workspace cleared. Ready to start next class update.');
    }
  };

  // ===== EFFECTS =====
  useEffect(() => {
    const storedTenant = localStorage.getItem('tenantId') || 'TYN31126910';
    setTenantId(storedTenant);

    const user = getUserFromStorage();
    if (user.id) {
      setUserId(user.id);
      setUserName(user.name);
    }
  }, [getUserFromStorage]);

  useEffect(() => {
    if (tenantId && sourceContext) {
      fetchPolicies();
      checkExistingPublishedResults();
    } else {
      if (!sourceContext) {
        setPublishedBatchId(null);
        setBatchResults(null);
        setIsResumedSession(false);
        setShowResults(false);
        setPromotionResults(null);
        setStudents([]);
        setPreviewData(null);
        setActiveStep(1);
      }
    }
  }, [tenantId, sourceContext, checkExistingPublishedResults, fetchPolicies]);

  const requiresManualAction = useMemo(() => {
    if (previewData?.requiresManualAction) return true;
    if ((batchResults?.summary?.yearBack || 0) > 0) return true;
    return false;
  }, [previewData, batchResults]);

  // ===== RENDER SUB-UNITS =====

  const renderStepIndicator = () => (
    <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100 overflow-x-auto shadow-inner mb-6 scrollbar-none">
      <div className="flex items-center justify-between min-w-[580px] px-2">
        {STEPS.map((step, index) => (
          <div key={step.id} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2.5">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all ${
                  activeStep >= step.id ? 'bg-indigo-600 text-white shadow-md shadow-indigo-100' : 'bg-slate-200 text-slate-500'
                } ${activeStep === step.id ? 'ring-4 ring-indigo-100 scale-105' : ''}`}
              >
                {activeStep > step.id ? '✓' : step.icon}
              </div>
              <span className={`text-xs whitespace-nowrap font-semibold ${activeStep >= step.id ? 'text-slate-800' : 'text-slate-400'}`}>
                {step.label}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`h-0.5 mx-4 flex-1 transition-colors ${activeStep > step.id ? 'bg-indigo-600' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderPublishedResultsBanner = () => {
    if (!publishedBatchId || !batchResults) return null;
    if (batchResults.isPromoted) return null;

    const summary = batchResults.summary || {};
    const publishedDate = batchResults.results?.[0]?.promotionDate
      ? new Date(batchResults.results[0].promotionDate).toLocaleDateString()
      : 'Recently';

    return (
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 backdrop-blur-sm p-5 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl">📤</span>
              <h4 className="text-sm font-bold text-indigo-900">Calculated Roster Ready — Final Move Required</h4>
              {isResumedSession && (
                <span className="rounded bg-amber-100 border border-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-800 uppercase">
                  Pending Session
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Saved calculation compiled on {publishedDate} · Group ID Code: <span className="font-mono font-bold text-slate-700 bg-white/80 px-1.5 py-0.5 rounded border border-slate-200">{publishedBatchId}</span>
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setSelectedBatchId(publishedBatchId);
                setShowBatchView(true);
                setActiveTab('batches');
              }}
              className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-50 transition shadow-sm"
            >
              🔍 Inspect Decisions
            </button>
            <button
              onClick={() => handleRollbackBatch(publishedBatchId)}
              disabled={loading}
              className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-100 transition"
            >
              ↩ Reset Entire Group
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 max-w-2xl">
          <div className="bg-white rounded-xl p-3 border border-indigo-100/50">
            <span className="block text-xs font-semibold text-slate-400">Total Checked</span>
            <span className="text-lg font-extrabold text-slate-800">{summary.total || 0} Students</span>
          </div>
          <div className="bg-white rounded-xl p-3 border border-indigo-100/50">
            <span className="block text-xs font-semibold text-slate-400">✅ Eligible to Move</span>
            <span className="text-lg font-extrabold text-emerald-600">{summary.promoted || 0} Students</span>
          </div>
          <div className="bg-white rounded-xl p-3 border border-indigo-100/50">
            <span className="block text-xs font-semibold text-slate-400">📚 With Backlogs</span>
            <span className="text-lg font-extrabold text-amber-600">{summary.promotedWithBacklog || 0} Students</span>
          </div>
          <div className="bg-white rounded-xl p-3 border border-indigo-100/50">
            <span className="block text-xs font-semibold text-slate-400">🔄 Repetitions (Year Back)</span>
            <span className="text-lg font-extrabold text-rose-600">{summary.yearBack || 0} Students</span>
          </div>
        </div>

        {requiresManualAction && (
          <div className="rounded-xl bg-amber-50/80 border border-amber-200 p-3.5 text-xs text-amber-800 font-medium max-w-2xl">
            ⚠️ Attention Required: This student group contains {summary.yearBack} repeating student(s). Make sure to mark their classroom target context destination below.
          </div>
        )}
      </div>
    );
  };

  const renderStudentTable = () => {
    if (!students || students.length === 0) return null;

    const total = students.length;
    const passed = students.filter(s => s.metrics?.isPassed).length;
    const failed = total - passed;
    const avgPercentage = total > 0
      ? Math.round(students.reduce((sum, s) => sum + (s.metrics?.overallPercentage || 0), 0) / total)
      : 0;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="rounded-xl bg-slate-50 border border-slate-200/60 p-3 text-center">
            <p className="text-xs font-semibold text-slate-500">Class Strengths</p>
            <p className="text-xl font-black text-slate-800">{total}</p>
          </div>
          <div className="rounded-xl bg-emerald-50/50 border border-emerald-100 p-3 text-center">
            <p className="text-xs font-semibold text-emerald-600">Passed Students</p>
            <p className="text-xl font-black text-emerald-700">{passed}</p>
          </div>
          <div className="rounded-xl bg-rose-50/50 border border-rose-100 p-3 text-center">
            <p className="text-xs font-semibold text-rose-600">Failed / Shortages</p>
            <p className="text-xl font-black text-rose-700">{failed}</p>
          </div>
          <div className="rounded-xl bg-indigo-600/5 border border-indigo-100 p-3 text-center">
            <p className="text-xs font-semibold text-indigo-600">Class Average</p>
            <p className="text-xl font-black text-indigo-700">{avgPercentage}%</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-slate-50/80 border-b border-slate-200 hidden md:block">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Loaded Marks Registry Ledger</p>
          </div>
          
          <div className="block md:hidden p-2 space-y-2 bg-slate-50">
            {students.map((student) => (
              <StudentDetailsRow key={student.studentId} student={student} />
            ))}
          </div>

          <table className="min-w-full divide-y divide-slate-200 text-sm hidden md:table">
            <thead className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">USN / ID</th>
                <th className="px-4 py-3 text-left">Full Name</th>
                <th className="px-4 py-3 text-left">Section</th>
                <th className="px-4 py-3 text-center">CIE</th>
                <th className="px-4 py-3 text-center">End Exam</th>
                <th className="px-4 py-3 text-center">Overall</th>
                <th className="px-4 py-3 text-center">Exam Result</th>
                <th className="px-4 py-3 text-center">Breakdown</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {students.map((student) => (
                <StudentDetailsRow key={student.studentId} student={student} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPreviewResults = () => {
    if (!previewData) return null;

    const summary = previewData.summary || {};
    const studentsPreview = previewData.students || [];

    return (
      <div className="space-y-4 bg-slate-50/50 border border-slate-200 rounded-2xl p-4">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Calculated Action Roster Preview</h4>
        
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm text-center">
            <p className="text-[10px] font-bold text-slate-400 uppercase">Roster Volume</p>
            <p className="text-lg font-extrabold text-slate-800">{summary.totalStudents || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm text-center">
            <p className="text-[10px] font-bold text-emerald-500 uppercase">Promoted</p>
            <p className="text-lg font-extrabold text-emerald-600">{summary.promoted || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm text-center">
            <p className="text-[10px] font-bold text-amber-500 uppercase">To Backlog Slot</p>
            <p className="text-lg font-extrabold text-amber-600">{summary.promotedWithBacklog || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm text-center">
            <p className="text-[10px] font-bold text-rose-400 uppercase">Year Back (Repeat)</p>
            <p className="text-lg font-extrabold text-rose-600">{summary.yearBack || 0}</p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-slate-500 font-semibold text-xs">
              <tr>
                <th className="px-4 py-2.5 text-left">USN</th>
                <th className="px-4 py-2.5 text-left">Student Name</th>
                <th className="px-4 py-2.5 text-center">Weighted %</th>
                <th className="px-4 py-2.5 text-center">Failed Courses</th>
                <th className="px-4 py-2.5 text-center">Target Status Assignment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-slate-700">
              {studentsPreview.map((student) => {
                const decision = student.decision || 'PENDING';
                const colors = {
                  PROMOTE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                  PROMOTE_WITH_BACKLOG: 'bg-amber-50 text-amber-700 border-amber-200',
                  YEAR_BACK: 'bg-rose-50 text-rose-700 border-rose-200',
                  PENDING: 'bg-slate-50 text-slate-500 border-slate-200',
                };
                return (
                  <tr key={student.studentId} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-2 font-mono text-xs font-semibold">{student.USN}</td>
                    <td className="px-4 py-2 font-medium">{student.name}</td>
                    <td className="px-4 py-2 text-center font-semibold">{student.overallPercentage || 0}%</td>
                    <td className="px-4 py-2 text-center font-bold text-slate-500">{student.failedSubjectsCount || 0}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold border ${colors[decision] || colors.PENDING}`}>
                        {decision.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {previewData.notification && (
          <div className={`rounded-xl p-3 text-xs border font-medium ${
            previewData.notification.type === 'WARNING' ? 'bg-amber-50 text-amber-800 border-amber-200' :
            previewData.notification.type === 'SUCCESS' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-blue-50 text-blue-800 border-blue-200'
          }`}>
            <p>{previewData.notification.message}</p>
          </div>
        )}
      </div>
    );
  };

  const renderPromotionControls = () => {
    const batchStudents = batchResults?.results || [];
    const yearBackStudents = batchStudents.filter(s => s.decision === 'YEAR_BACK');
    const promoteStudentsList = batchStudents.filter(
      s => s.decision === 'PROMOTE' || s.decision === 'PROMOTE_WITH_BACKLOG'
    );

    return (
      <div className="space-y-6">
        {isResumedSession && batchStudents.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex justify-between items-center">
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                Saved Verification List ({batchStudents.length} rows)
              </h4>
            </div>
            <div className="max-h-56 overflow-y-auto">
              <table className="min-w-full divide-y divide-slate-100 text-xs">
                <thead className="bg-slate-50 text-slate-500 font-semibold sticky top-0 border-b border-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left">USN</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-center">Total Percentage</th>
                    <th className="px-3 py-2 text-center">Assigned Action</th>
                    <th className="px-3 py-2 text-center">Reset Row</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                  {batchStudents.map(student => {
                    const colors = {
                      PROMOTE: 'bg-emerald-50 text-emerald-700',
                      PROMOTE_WITH_BACKLOG: 'bg-amber-50 text-amber-700',
                      YEAR_BACK: 'bg-rose-50 text-rose-700',
                    };
                    return (
                      <tr key={student.studentId} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-3 py-2 font-mono font-bold text-slate-900">{student.USN}</td>
                        <td className="px-3 py-2 font-medium">{student.name}</td>
                        <td className="px-3 py-2 text-center font-semibold">{student.overallPercentage || 0}%</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${colors[student.decision] || 'bg-slate-100 text-slate-600'}`}>
                            {student.decision?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => handleRollbackIndividual(student.studentId, publishedBatchId)}
                            className="text-rose-500 hover:text-rose-700 transition font-bold"
                          >
                            ✕ Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 space-y-3 shadow-inner">
          <div className="space-y-0.5">
            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <span>🎯</span> Destination Target Class for Moving Students
              {promoteStudentsList.length > 0 && (
                <span className="text-xs font-normal text-slate-500">({promoteStudentsList.length} students selected)</span>
              )}
            </h4>
            <p className="text-xs text-slate-400">Select which new academic track/year clean promotions or backlogged promotions drop into.</p>
          </div>
          
          <AcademicContextSelector
            tenantId={tenantId}
            onContextChange={setTargetContext}
            initialContext={targetContext}
            required={true}
            compact={true}
          />
          {targetContext && (
            <div className="text-xs font-bold text-emerald-600 bg-white p-2.5 rounded-lg border border-emerald-100 inline-block shadow-sm">
              Selected Target Route: {targetContext.programName} → {targetContext.academicYearName} → {targetContext.levelName}
            </div>
          )}
        </div>

        {requiresManualAction && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 space-y-4 shadow-sm">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="manualOverride"
                checked={manualOverride}
                onChange={(e) => setManualOverride(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-amber-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="space-y-0.5">
                <label htmlFor="manualOverride" className="text-sm font-bold text-slate-800 cursor-pointer">
                  Manually route Repeating Students (Year Back)
                  {yearBackStudents.length > 0 && (
                    <span className="ml-1 text-xs font-bold text-amber-700">({yearBackStudents.length} student entries)</span>
                  )}
                </label>
                <p className="text-xs text-slate-500">
                  Turn this on if you want to explicitly place repeating students in a special target track. Leaving this off places them automatically in their current level inside the next active academic calendar year.
                </p>
              </div>
            </div>

            {manualOverride && (
              <div className="pl-7 border-l-2 border-amber-300 space-y-2 animate-fadeIn">
                <div className="rounded-xl border border-amber-200 bg-white p-4 space-y-2 shadow-sm">
                  <h5 className="text-xs font-bold text-amber-800 uppercase tracking-wide">📍 Repeating Destination Target Track</h5>
                  <AcademicContextSelector
                    tenantId={tenantId}
                    onContextChange={setYearBackTargetContext}
                    initialContext={yearBackTargetContext}
                    required={true}
                    compact={true}
                  />
                  {yearBackTargetContext && (
                    <div className="text-xs font-bold text-emerald-600 bg-slate-50 p-2 rounded border border-emerald-100 inline-block">
                      Repeating Target Locked: {yearBackTargetContext.programName} → {yearBackTargetContext.academicYearName} → {yearBackTargetContext.levelName}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-slate-100 bg-white">
          <button
            onClick={handlePromote}
            disabled={
              loading ||
              !publishedBatchId ||
              !userId ||
              !targetContext ||
              (manualOverride && !yearBackTargetContext)
            }
            className="rounded-xl bg-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-md shadow-indigo-100 transition hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Executing Data Transfer...
              </>
            ) : (
              '🚀 Execute Student Promotion Move'
            )}
          </button>

          <div className="text-xs text-slate-400 space-y-0.5">
            {userId ? (
              <p className="font-medium text-slate-500">Authorized Operator: <span className="text-slate-700 font-bold">{userName}</span></p>
            ) : (
              <p className="text-rose-600 font-bold">❌ Error: Identity check missing. Operations blocked.</p>
            )}
            {publishedBatchId && (
              <p className="font-mono text-[10px]">Registry Stream Code: {publishedBatchId}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const data = promotionResults || (batchResults?.isPromoted ? batchResults : null);
    if (!data) return null;

    const studentsList = data.students || data.results || [];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="rounded-xl bg-slate-50 border p-3 text-center">
            <span className="block text-xs font-semibold text-slate-400">Total Grouped</span>
            <span className="text-xl font-black text-slate-800">{data.promotedCount || data.summary?.total || 0}</span>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
            <span className="block text-xs font-semibold text-emerald-600">Clean Passes</span>
            <span className="text-xl font-black text-emerald-700">{data.summary?.promoted || (data.promotedCount - (data.yearBackCount || 0)) || 0}</span>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center">
            <span className="block text-xs font-semibold text-amber-600">Moved with Backlog</span>
            <span className="text-xl font-black text-amber-700">{data.summary?.promotedWithBacklog || 0}</span>
          </div>
          <div className="rounded-xl bg-rose-50 border border-rose-100 p-3 text-center">
            <span className="block text-xs font-semibold text-rose-600">Retained (Repeaters)</span>
            <span className="text-xl font-black text-rose-700">{data.yearBackCount || data.summary?.yearBack || 0}</span>
          </div>
        </div>

        {studentsList.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">USN</th>
                  <th className="px-4 py-3 text-left">Student Name</th>
                  <th className="px-4 py-3 text-center">Calculated Action</th>
                  <th className="px-4 py-3 text-center">Target Class Slot</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Revert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white text-slate-700">
                {studentsList.map((student) => {
                  const colors = {
                    PROMOTE: 'bg-emerald-50 text-emerald-700',
                    PROMOTE_WITH_BACKLOG: 'bg-amber-50 text-amber-700',
                    YEAR_BACK: 'bg-rose-50 text-rose-700',
                  };
                  return (
                    <tr key={student.studentId} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-xs font-bold">{student.USN}</td>
                      <td className="px-4 py-2.5 font-medium">{student.name}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${colors[student.decision] || 'bg-slate-100 text-slate-700'}`}>
                          {student.decision?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center font-medium text-slate-600">{student.newClass || student.className || 'N/A'}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-bold ${
                          (student.isAllocated || student.isPromoted) ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {(student.isAllocated || student.isPromoted) ? '✓' : '⏳'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <button
                          onClick={() => handleRollbackIndividual(student.studentId, publishedBatchId)}
                          disabled={loading}
                          className="text-xs font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-40"
                        >
                          ✕ Reset Student
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-4 pt-3">
          <button
            onClick={handleStartNew}
            className="rounded-lg bg-slate-100 px-4 py-2 text-xs font-bold text-slate-700 border hover:bg-slate-200 transition"
          >
            🔄 Open Fresh Promotion Workspace
          </button>
          <button
            onClick={() => handleRollbackBatch(publishedBatchId)}
            disabled={loading}
            className="rounded-lg bg-rose-50 px-4 py-2 text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-100 transition disabled:opacity-40"
          >
            ↩ Rollback and Cancel Entire Group Move
          </button>
        </div>
      </div>
    );
  };

  const renderBacklogs = () => {
    if (!backlogs || backlogs.length === 0) return null;

    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50/30 p-4 space-y-2.5 shadow-sm max-w-2xl">
        <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
          <span>📚</span> Active Backlog Registry Carryovers ({backlogs.length})
        </h4>
        <div className="divide-y divide-amber-100/60 max-h-48 overflow-y-auto pr-1">
          {backlogs.map((backlog) => (
            <div key={backlog._id} className="flex items-center justify-between text-xs py-2 gap-4">
              <div className="space-y-0.5">
                <span className="font-mono font-bold text-slate-800 bg-white px-1.5 py-0.5 rounded border border-slate-200 mr-2">{backlog.studentUSN}</span>
                <span className="font-medium text-slate-700">{backlog.subjectName}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-slate-400 font-medium">Score: {backlog.originalPercentage}%</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide border ${
                  backlog.status === 'PENDING' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                  backlog.status === 'CLEARED' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-blue-100 text-blue-800 border-blue-200'
                }`}>
                  {backlog.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBatchView = () => {
    if (!batchResults) return null;

    const data = batchResults;
    const studentsList = data.results || [];

    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-md space-y-5 animate-fadeIn">
        <div className="flex items-start justify-between border-b border-slate-100 pb-3 flex-wrap gap-4">
          <div className="space-y-1">
            <h3 className="text-lg font-black text-slate-800">📊 Promotion Group Metrics Overview</h3>
            <p className="text-xs text-slate-400">System Batch Code Key: <span className="font-mono font-bold text-slate-700 bg-slate-100 px-1 py-0.5 rounded">{data.batchId}</span></p>
          </div>
          <button
            onClick={() => {
              setShowBatchView(false);
              setSelectedBatchId(null);
              setActiveTab('promotion');
            }}
            className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 transition"
          >
            ✕ Close View
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <div className="bg-slate-50 border p-3 rounded-xl text-center">
            <span className="block text-[10px] font-bold text-slate-400 uppercase">Roster Total</span>
            <span className="text-xl font-black text-slate-800">{data.summary?.total || 0}</span>
          </div>
          <div className="bg-emerald-50/60 border border-emerald-100 p-3 rounded-xl text-center">
            <span className="block text-[10px] font-bold text-emerald-600 uppercase">Promoted</span>
            <span className="text-xl font-black text-emerald-700">{data.summary?.promoted || 0}</span>
          </div>
          <div className="bg-amber-50/60 border border-amber-100 p-3 rounded-xl text-center">
            <span className="block text-[10px] font-bold text-amber-600 uppercase">With Backlog</span>
            <span className="text-xl font-black text-amber-700">{data.summary?.promotedWithBacklog || 0}</span>
          </div>
          <div className="bg-rose-50/60 border border-rose-100 p-3 rounded-xl text-center">
            <span className="block text-[10px] font-bold text-rose-600 uppercase">Year Backs</span>
            <span className="text-xl font-black text-rose-700">{data.summary?.yearBack || 0}</span>
          </div>
          <div className="bg-indigo-50 border border-indigo-100 p-3 rounded-xl text-center">
            <span className="block text-[10px] font-bold text-indigo-600 uppercase">Process Status</span>
            <span className="text-base font-black text-indigo-800 pt-0.5 block">{data.isPromoted ? 'Finished ✓' : 'Published'}</span>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">USN</th>
                <th className="px-4 py-3 text-left">Full Name</th>
                <th className="px-4 py-3 text-center">Decision Status</th>
                <th className="px-4 py-3 text-center">Weighted Score</th>
                <th className="px-4 py-3 text-center">Fails Count</th>
                <th className="px-4 py-3 text-center">Database State</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white text-slate-700">
              {studentsList.map((student) => {
                const colors = {
                  PROMOTE: 'bg-emerald-50 text-emerald-700',
                  PROMOTE_WITH_BACKLOG: 'bg-amber-50 text-amber-700',
                  YEAR_BACK: 'bg-rose-50 text-rose-700',
                };
                return (
                  <tr key={student.studentId} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs font-bold">{student.USN}</td>
                    <td className="px-4 py-2.5 font-medium">{student.name}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${colors[student.decision] || 'bg-slate-100 text-slate-700'}`}>
                        {student.decision?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center font-semibold">{student.overallPercentage || 0}%</td>
                    <td className="px-4 py-2.5 text-center font-bold text-slate-500">{student.failedSubjectsCount || 0}</td>
                    <td className="px-4 py-2.5 text-center font-extrabold text-indigo-600">{student.isPromoted ? '✓ Saved' : '⏳ Ready'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <button
                        onClick={() => handleRollbackIndividual(student.studentId, data.batchId)}
                        disabled={loading}
                        className="text-xs font-bold text-rose-500 hover:text-rose-700 disabled:opacity-40"
                      >
                        ✕ Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={() => handleRollbackBatch(data.batchId)}
            disabled={loading}
            className="rounded-lg bg-rose-50 px-4 py-2 text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-100 transition disabled:opacity-40"
          >
            ↩ Revert Batch Records
          </button>
          {!data.isPromoted && data.isPublished && (
            <button
              onClick={() => {
                setPublishedBatchId(data.batchId);
                setShowBatchView(false);
                setActiveTab('promotion');
                setActiveStep(5);
              }}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow hover:bg-indigo-700 transition"
            >
              🚀 Continue to Promotion Move Form →
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderBatchList = () => (
    <div className="space-y-4 animate-fadeIn">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-base font-black text-slate-800">📋 Promotion Registry Archives</h3>
          <p className="text-xs text-slate-400">Review historical drafts, published batches, and completed logs.</p>
        </div>
        <button
          onClick={() => {
            setShowBatchList(false);
            setActiveTab('promotion');
          }}
          className="rounded-lg bg-slate-100 border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 transition shadow-sm"
        >
          ← Return to Promotion Desk
        </button>
      </div>
      <BatchList
        tenantId={tenantId}
        onSelectBatch={(batchId) => {
          fetchBatchResults(batchId);
          setShowBatchList(false);
        }}
      />
    </div>
  );

  const renderConfirmModal = () => {
    if (!showConfirmModal || !modalData) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-slate-100 space-y-4">
          <div className="space-y-1.5">
            <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
              <span className="text-rose-500">⚠</span> {modalData.title}
            </h3>
            <p className="text-xs text-slate-500 leading-relaxed">{modalData.message}</p>
          </div>
          <div className="flex justify-end gap-2.5 pt-2">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="rounded-xl border bg-slate-50 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
            >
              {modalData.cancelText || 'Cancel'}
            </button>
            <button
              onClick={confirmRollback}
              className={`rounded-xl px-4 py-2 text-xs font-bold text-white shadow-sm transition ${
                modalData.action === 'rollbackBatch' || modalData.action === 'rollbackIndividual'
                  ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {modalData.confirmText || 'Confirm Action'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl p-3 md:p-6 space-y-6 text-slate-800 antialiased selection:bg-indigo-100">
      
      {/* Header Panel */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-xl md:text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <span>🎓</span> Student Promotion Workspace
          </h1>
          <p className="text-xs text-slate-400 font-medium">Evaluate final semester/term scores and transition student cohorts into upcoming levels.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-xl bg-slate-100 p-1 border border-slate-200/40">
            <button
              onClick={() => {
                setActiveTab('promotion');
                setShowBatchView(false);
                setShowBatchList(false);
              }}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                activeTab === 'promotion' && !showBatchView && !showBatchList
                  ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              🚀 Promotion Desk
            </button>
            <button
              onClick={() => {
                setActiveTab('batches');
                setShowBatchList(true);
                setShowBatchView(false);
              }}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                activeTab === 'batches' || showBatchView
                  ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              📋 View Batches
            </button>
          </div>

          <div className="flex gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 p-1.5 rounded-xl border">
            {userId && <span className="bg-white border rounded px-1.5 py-0.5 text-slate-600 shadow-sm">👤 {userName}</span>}
            <span className="bg-white border rounded px-1.5 py-0.5 text-slate-500 shadow-sm">{tenantId}</span>
          </div>
        </div>
      </div>

      {/* Conditional Batch Tabs Views */}
      {activeTab === 'batches' && (showBatchList || showBatchView) && (
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
          {showBatchView && renderBatchView()}
          {showBatchList && !showBatchView && renderBatchList()}
        </div>
      )}

      {/* Main Promotion Operations Workspace */}
      {activeTab === 'promotion' && !showBatchView && (
        <>
          {renderStepIndicator()}

          {/* Toast Alerts */}
          {error && (
            <div className="rounded-xl bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-800 border border-rose-200/60 shadow-sm animate-shake">
              ⚠️ Alert: {error}
            </div>
          )}
          {success && (
            <div className="rounded-xl bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-800 border border-emerald-200/60 shadow-sm">
              ✅ Success: {success}
            </div>
          )}

          {contextCheckLoading && (
            <div className="rounded-xl bg-blue-50/80 backdrop-blur-sm px-4 py-3 text-xs font-medium text-blue-800 border border-blue-200 flex items-center gap-2.5 animate-pulse">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
              Scanning database servers for pre-existing records for this group context...
            </div>
          )}

          {/* Source Class Selection */}
          <div className="space-y-2">
            <div className="space-y-0.5">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <span>📍</span> 1. Select Starting Student Class Group (Source)
              </h3>
              <p className="text-xs text-slate-400">Specify exactly which department cohort and year level you are currently assessing.</p>
            </div>
            <AcademicContextSelector
              tenantId={tenantId}
              onContextChange={setSourceContext}
              initialContext={sourceContext}
              required={true}
              compact={true}
            />
            {sourceContext && (
              <div className="text-xs font-bold text-emerald-600 bg-white border border-emerald-100 rounded-lg px-3 py-1.5 inline-block shadow-sm animate-fadeIn">
                Source Locked: {sourceContext.programName} → {sourceContext.academicYearName} → {sourceContext.levelName}
              </div>
            )}
          </div>

          {/* Resume Session Banner */}
          {renderPublishedResultsBanner()}

          {/* Direct Promotion Block Form Display */}
          {publishedBatchId && batchResults && !batchResults.isPromoted && !showResults && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4 animate-slideDown">
              <div className="flex items-center justify-between border-b pb-2 flex-wrap gap-2">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">🚀 Final Step: Set Destinies & Commit Transfer</h3>
                <button
                  onClick={handleStartNew}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition"
                >
                  🔄 Cancel & Open Blank Session instead
                </button>
              </div>
              {renderPromotionControls()}
              {renderBacklogs()}
            </div>
          )}

          {/* Clean Step Processing Path Flow */}
          {!publishedBatchId && !contextCheckLoading && (
            <div className="space-y-6">
              
              {/* Load Student Cohort Profile Base */}
              {sourceContext && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4 animate-fadeIn">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-3">
                    <div className="space-y-0.5">
                      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                        <span>👨‍🎓</span> 2. Load and Audit Student Mark Logs
                      </h3>
                      <p className="text-xs text-slate-400">Pull historical mark distributions to auto-verify pass criteria ratios.</p>
                    </div>
                    <button
                      onClick={fetchStudents}
                      disabled={loading}
                      className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-indigo-100 hover:bg-indigo-700 transition disabled:opacity-40"
                    >
                      {loading ? 'Refreshing...' : '📥 Fetch Marks Registry'}
                    </button>
                  </div>
                  {students.length > 0 && renderStudentTable()}
                </div>
              )}

              {/* Rules Template Maps Selection and Actions Triggering */}
              {students.length > 0 && (
                <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4 animate-fadeIn">
                  <div className="space-y-0.5">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                      <span>📋</span> 3. Select Promotion Rule and Calculate Mock Preview
                    </h3>
                    <p className="text-xs text-slate-400">Choose the matching academic policy cutoff parameters configured for this syllabus core.</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 bg-slate-50 p-3 rounded-xl border">
                    <select
                      value={selectedPolicyId}
                      onChange={(e) => setSelectedPolicyId(e.target.value)}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 font-medium focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    >
                      <option value="">Choose Policy Template</option>
                      {policies.map(p => (
                        <option key={p._id} value={p._id}>{p.policyName}</option>
                      ))}
                    </select>

                    <button
                      onClick={handlePreview}
                      disabled={loading || !selectedPolicyId}
                      className="rounded-xl bg-indigo-50 px-4 py-2 text-xs font-bold text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition disabled:opacity-40"
                    >
                      👀 Run Mock Preview Calculation
                    </button>

                    {previewData && (
                      <button
                        onClick={handlePublish}
                        disabled={loading || !userId}
                        className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-emerald-100 hover:bg-emerald-700 transition disabled:opacity-40"
                      >
                        📤 Confirm & Publish This Roster List
                      </button>
                    )}
                  </div>

                  {previewData && <div className="mt-2 animate-fadeIn">{renderPreviewResults()}</div>}
                </div>
              )}
            </div>
          )}

          {/* Historical Summary Display Cards */}
          {showResults && (promotionResults || batchResults?.isPromoted) && (
            <div className="bg-white rounded-2xl border border-emerald-200 p-5 shadow-sm space-y-4 animate-slideDown">
              <div className="flex items-center justify-between border-b pb-2 flex-wrap gap-2">
                <h3 className="text-sm font-black text-slate-900 flex items-center gap-1.5">
                  <span>📊</span> Summary Ledger of Completed Moves
                </h3>
                <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                  Transaction Successfully Confirmed ✓
                </span>
              </div>
              {renderResults()}
              {renderBacklogs()}
            </div>
          )}
        </>
      )}

      {renderConfirmModal()}

      {/* Loading Block Overlays */}
      {loading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/20 backdrop-blur-xs">
          <div className="rounded-2xl bg-white p-5 shadow-2xl border border-slate-100 text-center space-y-3 max-w-xs animate-scaleUp">
            <div className="h-9 w-9 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent mx-auto" />
            <p className="text-xs font-bold text-slate-700">Updating academic logs. Please stand by...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegularPromotion;